import React, { useState, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Container,
  IconButton,
} from "@mui/material";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import CircleIcon from "@mui/icons-material/Circle";
import { useParams, Navigate } from "react-router-dom";
import { PROJECTS_MAP } from "../assets/contents";

const ProjectSection = ({ project }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 90%", "end 10%"],
  });

  const smoothProg = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 20,
    mass: 0.25,
  });

  const imgY = useTransform(smoothProg, [0, 1], ["-25px", "25px"]);

  const handleDotClick = (index) => {
    if (index === activeIndex) return;
    setIsFading(true);
    setTimeout(() => {
      setActiveIndex(index);
      setIsFading(false);
    }, 140);
  };

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 5, sm: 7, md: 9 },
        px: { xs: 2.5, sm: 5, md: 7 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Grid
        container
        spacing={{ xs: 4, sm: 5, md: 7 }}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          maxWidth: "1200px",
          width: "100%",
          flexDirection: { xs: "column", sm: "column", md: "row" },
        }}
      >
        {/* LEFT TEXT */}
        <Grid item xs={12} md={6}>
          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
            <Typography
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 600,
                color: "#b1976b",
                mb: 1,
                fontSize: {
                  xs: "1.7rem",
                  sm: "2rem",
                  md: "2.5rem",
                },
              }}
            >
              {project.head}
            </Typography>

            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                mb: 2,
                color: "#000",
                letterSpacing: 1,
                fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
              }}
            >
              {project.content}
            </Typography>
          </Box>
        </Grid>

        {/* RIGHT IMAGE + DOTS */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            // force a real gap on desktop for EVERY project
            pl: { md: 4, lg: 5 },
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              // important: prevent the image from collapsing if text gets wide
              minWidth: { md: 420, lg: 460 },
              width: { xs: "100%", sm: "92%", md: "auto" },
            }}
          >
            {project.imagesSet?.length > 0 && (
              <>
                {/* IMAGE FRAME */}
                <Box
                  sx={{
                    overflow: "hidden",
                    position: "relative",
                    borderRadius: "12px",
                    width: {
                      xs: "100%",
                      sm: "92%",
                      md: 440,
                      lg: 500,
                    },
                    height: {
                      xs: 230,
                      sm: 300,
                      md: 330,
                      lg: 380,
                    },
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                    backgroundColor: "#ddd",
                  }}
                >
                  <motion.img
                    src={project?.imagesSet[activeIndex]}
                    alt={project.head}
                    style={{
                      y: imgY,
                      width: "100%",
                      height: "150%",
                      objectFit: "cover",
                      opacity: isFading ? 0.4 : 1,
                      transition: "opacity 0.25s ease-out",
                      willChange: "transform, opacity",
                      backfaceVisibility: "hidden",
                    }}
                  />
                </Box>

                {/* DOTS */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "center", md: "flex-start" },
                    mt: 2,
                    gap: 1.5,
                    width: {
                      xs: "100%",
                      sm: "92%",
                      md: 440,
                      lg: 500,
                    },
                  }}
                >
                  {project?.imagesSet.map((_, index) => (
                    <IconButton
                      key={index}
                      onClick={() => handleDotClick(index)}
                      size="small"
                      sx={{
                        color:
                          index === activeIndex ? "#b1976b" : "rgba(0,0,0,0.3)",
                        transition: "color 0.3s ease",
                      }}
                    >
                      <CircleIcon sx={{ fontSize: 10 }} />
                    </IconButton>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const ExploreProjects = () => {
  const { id } = useParams();

  const projectsData = PROJECTS_MAP[id];

  // safety fallback
  if (!projectsData) {
    return <Navigate to="/projects" replace />;
  }

  return (
    <Box sx={{ backgroundColor: "#fff", width: "100%" }}>
      <Container maxWidth="xl" disableGutters>
        <Box
          sx={{
            display: "flex",
            px: { xs: 3, md: 12 },
            py: { xs: 2, md: 5 },
            backgroundColor: "#fff",
            justifyContent: "center",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#b2925a",
                fontFamily: "serif",
                flex: 1,
                textAlign: { xs: "center", md: "left" },
              }}
            >
              {projectsData.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                color: "#000",
                letterSpacing: 1,
                fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
              }}
            >
              {projectsData.location}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            px: { xs: 3, md: 12 },
            py: { xs: 3, md: 5 },
            backgroundColor: "#fff",
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          <Box
            sx={{
              flex: 2,
              display: projectsData.objective !== "" ? "flex" : "block",
              flexDirection: projectsData.objective === "" ? "row" : "column",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#b2925a",
                fontFamily: "serif",
                flex: 1,
                textAlign: { xs: "center", md: "left" },
                fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2rem" },
              }}
            >
              Socpe:
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                mb: 2,
                color: "#000",
                letterSpacing: 1,
                fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
              }}
            >
              {projectsData.scope}
            </Typography>
          </Box>
          {projectsData.objective !== "" && (
            <Box sx={{ flex: 2 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#b2925a",
                  fontFamily: "serif",
                  flex: 1,
                  textAlign: { xs: "center", md: "left" },
                  fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2rem" },
                }}
              >
                Objective:
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  mb: 2,
                  color: "#000",
                  letterSpacing: 1,
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                }}
              >
                {projectsData.objective}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            px: { xs: 3, md: 12 },
            py: { xs: 3, md: 6 },
            backgroundColor: "#fff",
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          <Box sx={{ flex: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#b2925a",
                fontFamily: "serif",
                flex: 1,
                textAlign: { xs: "center", md: "left" },
                fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2rem" },
              }}
            >
              Execution:
            </Typography>
          </Box>
          <Box sx={{ flex: 2 }}>
            <Typography
              variant="body1"
              sx={{ color: "#333", textAlign: "justify" }}
            >
              {projectsData.execution}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            px: { xs: 3, md: 12 },
            py: { xs: 3, md: 6 },
            backgroundColor: "#fff",
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          <Box sx={{ flex: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#b2925a",
                fontFamily: "serif",
                flex: 1,
                textAlign: { xs: "center", md: "left" },
                fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2rem" },
              }}
            >
              The Mandate:
            </Typography>
          </Box>
          <Box sx={{ flex: 2 }}>
            <Typography
              variant="body1"
              sx={{ color: "#333", textAlign: "justify" }}
            >
              {projectsData.mandate}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#b2925a",
            fontFamily: "serif",
            flex: 1,
            textAlign: "center",
            textDecoration: "underline",
            mt: 4,
          }}
        >
          {projectsData.helightTitle}
        </Typography>

        {projectsData?.highlights.map((proj, idx) => (
          <ProjectSection key={idx} project={proj} />
        ))}
      </Container>
    </Box>
  );
};

export default ExploreProjects;
